import React, { useEffect, useRef } from 'react';
import { EventModel } from './useEventManagement';
import { EVENT_TYPES, STATUS_OPTIONS } from './eventTypes';
import EventDetailPane from './EventDetailPane';
import './EventWorkspace.css';

const EventListAndDetail: React.FC<{ model: EventModel }> = ({ model }) => {
  const {
    events, loading, selectedEvent,
    filterStatus, setFilterStatus, filterType, setFilterType, searchQuery, setSearchQuery,
    notification, setNotification,
    fetchEvents, fetchEventDetail, openCreateForm,
    getStatusBadge, getStatusLabel,
    formatShortDate, totalEvents, scheduledCount, activeCount,
    completedCount, cancelledCount, inputStyle,
  } = model;

  const listRef = useRef<HTMLDivElement>(null);
  const panesRef = useRef<HTMLDivElement>(null);
  const detailRef = useRef<HTMLDivElement>(null);

  // When a selection lands while the panes are stacked, bring the detail
  // card into view. In split mode both panes are already visible.
  const selectedId = selectedEvent?.id;
  useEffect(() => {
    if (!selectedId || !panesRef.current || !detailRef.current) return;
    const panes = panesRef.current.getBoundingClientRect();
    const detail = detailRef.current.getBoundingClientRect();
    const stacked = detail.top >= panes.top + 40;
    if (stacked) detailRef.current.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  }, [selectedId]);

  // Stack-only control: returns focus/scroll to the list without clearing
  // the selection (the detail stays rendered below the list).
  const returnToList = () => {
    const el = listRef.current;
    if (!el) return;
    el.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    const current = el.querySelector<HTMLElement>('[data-selected="true"]') || el.querySelector<HTMLElement>('button');
    current?.focus({ preventScroll: true });
  };

  return (
    <div className="admin-events">
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg border flex items-center gap-3 shadow-lg max-w-lg ${
          notification.type === 'error' ? 'bg-red-600/20 text-red-400 border-red-500/30' : 'bg-green-600/20 text-green-400 border-green-500/30'
        }`}>
          <span className="text-sm flex-1">{notification.message}</span>
          <button onClick={() => navigator.clipboard.writeText(notification.message)} className="text-current hover:opacity-70 text-xs" title="Copy">Copy</button>
          <button onClick={() => setNotification(null)} className="text-current hover:opacity-70" aria-label="Dismiss notification">×</button>
        </div>
      )}

      {/* Stats strip */}
      <div className="rp-bento-card events-summary">
        {[
          { label: 'Total Events', value: totalEvents, color: 'var(--rp-text)' },
          { label: 'Scheduled', value: scheduledCount, color: '#3b82f6' },
          { label: 'Active', value: activeCount, color: '#10b981' },
          { label: 'Completed', value: completedCount, color: '#6b7280' },
          { label: 'Cancelled', value: cancelledCount, color: '#ef4444' },
        ].map(stat => (
          <div key={stat.label} className="events-summary-item">
            <span className="events-summary-value" style={{ color: stat.color }}>{stat.value}</span>
            <span className="events-summary-label">{stat.label}</span>
          </div>
        ))}
      </div>

      {/* Actions & Filters */}
      <div className="rp-bento-card events-toolbar">
        <div className="events-filters">
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search events..."
            aria-label="Search events"
            className="events-search px-3 py-2 rounded-lg text-sm border border-white/10 focus:border-blue-400/50 focus:outline-none"
            style={inputStyle}
          />
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            aria-label="Filter by event status"
            className="px-3 py-2 rounded-lg text-sm border border-white/10" style={inputStyle}>
            <option value="all">All Statuses</option>
            {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select value={filterType} onChange={e => setFilterType(e.target.value)}
            aria-label="Filter by event type"
            className="px-3 py-2 rounded-lg text-sm border border-white/10" style={inputStyle}>
            <option value="all">All Types</option>
            {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div className="events-actions">
          <button onClick={fetchEvents} disabled={loading}
            className="admin-btn admin-btn-secondary">
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          <button onClick={openCreateForm}
            className="admin-btn admin-btn-primary">
            + Create Event
          </button>
        </div>
      </div>

      {/* Event List + Detail (DOM order: list, then detail) */}
      <div className="events-panes" ref={panesRef}>
        {/* Event List */}
        <div className="events-pane" ref={listRef}>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-white rounded-full mx-auto mb-4"></div>
              <p style={{ color: 'var(--rp-text-secondary)' }}>Loading events...</p>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-12 rp-bento-card">
              <p style={{ color: 'var(--rp-text-secondary)' }}>No events found</p>
            </div>
          ) : (
            events.map(event => (
              <button
                key={event.id}
                onClick={() => fetchEventDetail(event.id)}
                data-selected={selectedEvent?.id === event.id}
                className="events-item"
              >
                <div className="events-item-head">
                  <h3 className="events-item-title" style={{ color: 'var(--rp-text)' }}>{event.title}</h3>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold border whitespace-nowrap ${getStatusBadge(event.status)}`}>
                    {getStatusLabel(event.status)}
                  </span>
                </div>
                <div className="events-item-meta">
                  <span>{EVENT_TYPES.find(t => t.value === event.event_type)?.label || event.event_type}</span>
                  <span>{formatShortDate(event.meetup_time)}</span>
                  {event.city && <span>{event.city}, {event.state}</span>}
                </div>
                <div className="events-item-meta" style={{ color: 'var(--rp-text-tertiary)' }}>
                  <span>Host: {event.host_name || event.host_email || 'Unknown'}</span>
                  <span>{event.participant_count} riders</span>
                  {event.sponsor_count > 0 && <span>{event.sponsor_count} sponsors</span>}
                </div>
              </button>
            ))
          )}
        </div>

        {/* Event Detail Panel — always rendered after the list (stack or split) */}
        <div className="events-pane" ref={detailRef}>
          <EventDetailPane model={model} onReturnToList={returnToList} />
        </div>
      </div>

    </div>
  );
};

export default EventListAndDetail;
